/**
 * NewcomerPage – Pre-A1 flashcard deck for true beginners.
 *
 * Flow:
 *   GET /api/newcomer/cards?limit=10  → today's batch (review + fresh)
 *   On flip: front shows lemma + icon/display; back shows translation,
 *            respelling, audio (TTS) and a Press-to-record button.
 *   On rating: POST /api/newcomer/review → next card.
 *   On pronounce: POST /api/newcomer/pronounce → similarity banner.
 *
 * When the deck is exhausted we fetch the next batch. Once the backend
 * promotes the user (newcomer_completed=true), HomePage stops gating Free
 * Talk on Newcomer.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CaretLeft,
  ArrowRight,
  SpeakerHigh,
  Microphone,
  Stop,
  CheckCircle,
  Check,
  Sparkle,
  SpinnerGap,
  Translate,
} from '@phosphor-icons/react';
import * as Phosphor from '@phosphor-icons/react';
import { http, API, apiErrorMessage, getStoredToken } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import useRecorder from '@/hooks/useRecorder';

const CATEGORY_LABEL = {
  greetings: 'Greetings',
  numbers: 'Numbers',
  days: 'Days of the week',
  first_words: 'First words',
  survival: 'Survival phrases',
};

export default function NewcomerPage() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ mastered: 0, total_cards: 50 });
  const [speaking, setSpeaking] = useState(false);
  const [pronScore, setPronScore] = useState(null); // { score, label, heard }
  const [submitting, setSubmitting] = useState(false);
  const recorder = useRecorder();
  const card = cards[idx] || null;

  // --- load deck + progress ---
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [a, b] = await Promise.all([
          http.get('/newcomer/cards', { params: { limit: 10 } }),
          http.get('/newcomer/progress'),
        ]);
        setCards(a.data?.items || []);
        setProgress(b.data || progress);
      } catch (e) {
        setError(apiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadNextBatch() {
    setSubmitting(true);
    try {
      const [a, b] = await Promise.all([
        http.get('/newcomer/cards', { params: { limit: 10 } }),
        http.get('/newcomer/progress'),
      ]);
      setCards(a.data?.items || []);
      setProgress(b.data || progress);
      setIdx(0);
      setFlipped(false);
      setPronScore(null);
      // Pull updated user state — we may have just graduated.
      await refresh?.();
    } finally {
      setSubmitting(false);
    }
  }

  function nextCard() {
    setFlipped(false);
    setPronScore(null);
    if (idx + 1 < cards.length) {
      setIdx(idx + 1);
    } else {
      loadNextBatch();
    }
  }

  // --- speak (TTS) ---
  async function speakCard(text, slow = false) {
    if (!text || speaking) return;
    setSpeaking(true);
    try {
      const token = getStoredToken();
      const res = await fetch(`${API}/tts`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text, speed: slow ? 0.75 : 0.95 }),
      });
      if (!res.ok) throw new Error('tts failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); setSpeaking(false); };
      audio.onerror = () => { URL.revokeObjectURL(url); setSpeaking(false); };
      await audio.play();
    } catch {
      setSpeaking(false);
    }
  }

  // --- pronounce ---
  async function startRecord() {
    setPronScore(null);
    try {
      await recorder.start();
    } catch (e) {
      setError(e?.message || 'Microphone permission denied');
    }
  }

  async function stopRecord() {
    try {
      const blob = await recorder.stop();
      if (!blob || !card) return;
      // Send to /transcribe to get the heard text, then score it.
      setSubmitting(true);
      const fd = new FormData();
      fd.append('audio', blob, 'card.webm');
      const token = getStoredToken();
      const res = await fetch(`${API}/transcribe`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const j = await res.json();
      const heard = (j?.text || '').trim();
      const { data } = await http.post('/newcomer/pronounce', {
        card_id: card.id,
        target: card.lemma,
        heard,
      });
      setPronScore(data);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  // --- rate ---
  async function rate(rating) {
    if (!card) return;
    setSubmitting(true);
    try {
      await http.post('/newcomer/review', { card_id: card.id, rating });
      // Optimistic progress bump on "good"/"easy"
      if (rating !== 'again' && !card.progress?.mastered) {
        setProgress((p) => ({
          ...p,
          mastered: Math.min((p.mastered || 0) + (rating === 'easy' ? 1 : 0.34), p.total_cards),
        }));
      }
      nextCard();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  // --- visuals ---
  const IconCmp = useMemo(() => {
    if (!card?.icon) return null;
    return Phosphor[card.icon] || null;
  }, [card]);

  // Graduation screen — after all 50 cards are mastered, push to Sound
  // Foundations (the next Newcomer step) instead of Home. The full
  // level_kind→regular transition happens once Foundations is also done.
  if (!loading && (user?.newcomer_flashcards_completed || progress.completion_pct >= 100)) {
    const foundationsDone = !!user?.foundations_completed;
    return (
      <div className="lf-shell flex flex-col px-6 py-10" data-testid="newcomer-graduation">
        <BackBar />
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="lf-card mt-12 p-8 text-center bg-gradient-to-br from-[#FFF5EE] to-[#F7EFE9]"
        >
          <div className="grid h-20 w-20 mx-auto place-items-center rounded-full bg-[var(--lf-primary)] text-white">
            <Sparkle size={36} weight="fill" />
          </div>
          <h2 className="mt-5 text-3xl font-bold">¡Bien hecho! Flashcards done.</h2>
          <p className="mt-2 text-[var(--lf-text-muted)]">
            You've mastered all {progress.total_cards} starter cards. Next up: {foundationsDone ? 'start your first conversation!' : 'get your sounds right in 5 short lessons.'}
          </p>
          <button
            onClick={() => navigate(foundationsDone ? '/home' : '/foundations')}
            className="lf-btn lf-btn-primary mt-6"
            data-testid="newcomer-graduation-cta"
          >
            {foundationsDone ? 'Start practising' : 'Continue to Sound Foundations'} <ArrowRight size={18} weight="bold" />
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="lf-shell flex flex-col px-6 py-8" data-testid="newcomer-page">
      <BackBar />

      {/* Progress strip */}
      <div className="mt-2 mb-5 rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface)] p-3" data-testid="newcomer-progress">
        <div className="flex items-center justify-between text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
          <span>Newcomer pack</span>
          <span data-testid="newcomer-progress-count">
            {Math.floor(progress.mastered || 0)} / {progress.total_cards || 50}
          </span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--lf-surface-muted)]">
          <motion.div
            className="h-full bg-[var(--lf-primary)]"
            initial={{ width: 0 }}
            animate={{
              width: `${Math.min(100, ((progress.mastered || 0) / (progress.total_cards || 50)) * 100)}%`,
            }}
            transition={{ duration: 0.4 }}
            data-testid="newcomer-progress-bar"
          />
        </div>
      </div>

      {error ? <div className="mb-3 text-sm text-[var(--lf-destructive)]">{error}</div> : null}

      {/* Loading / empty / card */}
      {loading ? (
        <Loading />
      ) : !card ? (
        <Empty onReload={loadNextBatch} />
      ) : (
        <>
          <div className="mb-2 text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
            {CATEGORY_LABEL[card.category] || card.category} · card {idx + 1} of {cards.length}
          </div>

          <AnimatePresence mode="wait">
            <motion.button
              key={card.id + (flipped ? '-back' : '-front')}
              initial={{ rotateY: flipped ? -90 : 90, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ rotateY: flipped ? 90 : -90, opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setFlipped((f) => !f)}
              className="lf-card w-full p-8 text-left active:scale-[0.99]"
              style={{ minHeight: 280 }}
              data-testid={flipped ? 'newcomer-card-back' : 'newcomer-card-front'}
            >
              {!flipped ? (
                <CardFront card={card} IconCmp={IconCmp} />
              ) : (
                <CardBack card={card} pronScore={pronScore} />
              )}
            </motion.button>
          </AnimatePresence>

          {/* Action row — depends on side */}
          <div className="mt-5 space-y-3">
            <div className="flex gap-2">
              <button
                onClick={() => speakCard(card.lemma, false)}
                disabled={speaking}
                className="lf-btn lf-btn-secondary flex-1"
                data-testid="newcomer-listen"
              >
                {speaking ? (
                  <SpinnerGap className="animate-spin" size={16} />
                ) : (
                  <SpeakerHigh size={16} weight="fill" />
                )}
                Listen
              </button>
              <button
                onClick={() => speakCard(card.lemma, true)}
                disabled={speaking}
                className="lf-btn lf-btn-ghost flex-1"
                data-testid="newcomer-listen-slow"
              >
                Slow
              </button>
            </div>

            {flipped ? (
              <div className="space-y-3">
                {!recorder.recording ? (
                  <button
                    onClick={startRecord}
                    disabled={submitting}
                    className="lf-btn lf-btn-primary w-full py-3"
                    data-testid="newcomer-mic-start"
                  >
                    <Microphone size={18} weight="fill" /> Press & say it
                  </button>
                ) : (
                  <button
                    onClick={stopRecord}
                    className="lf-btn lf-btn-destructive w-full py-3"
                    data-testid="newcomer-mic-stop"
                  >
                    <Stop size={18} weight="fill" /> Stop & score
                  </button>
                )}

                {pronScore ? (
                  <ScoreBanner pronScore={pronScore} target={card.lemma} />
                ) : null}

                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => rate('again')}
                    disabled={submitting}
                    className="lf-btn lf-btn-ghost py-3"
                    data-testid="newcomer-rate-again"
                  >
                    Again
                  </button>
                  <button
                    onClick={() => rate('good')}
                    disabled={submitting}
                    className="lf-btn lf-btn-secondary py-3"
                    data-testid="newcomer-rate-good"
                  >
                    Good
                  </button>
                  <button
                    onClick={() => rate('easy')}
                    disabled={submitting}
                    className="lf-btn lf-btn-primary py-3"
                    data-testid="newcomer-rate-easy"
                  >
                    Easy <Check size={14} weight="bold" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setFlipped(true)}
                className="lf-btn lf-btn-primary w-full py-3"
                data-testid="newcomer-flip"
              >
                Show meaning
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
function BackBar() {
  return (
    <div className="flex items-center gap-2">
      <Link
        to="/home"
        className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]"
        data-testid="newcomer-back"
      >
        <CaretLeft size={18} />
      </Link>
      <h1 className="text-2xl font-bold">Start from scratch</h1>
    </div>
  );
}

function CardFront({ card, IconCmp }) {
  // First-words cards show a Phosphor icon. Numbers/days show big text.
  // Greetings/survival show the lemma huge.
  return (
    <div className="flex h-full flex-col items-center justify-center text-center" style={{ minHeight: 220 }}>
      {card.display ? (
        <div className="text-7xl font-black tracking-tight text-[var(--lf-primary)]" data-testid="newcomer-card-display">
          {card.display}
        </div>
      ) : IconCmp ? (
        <div className="grid h-24 w-24 place-items-center rounded-full bg-[var(--lf-surface-muted)] text-[var(--lf-primary)]">
          <IconCmp size={56} weight="duotone" />
        </div>
      ) : null}
      <div className={`mt-${card.display || IconCmp ? '5' : '0'} text-3xl font-bold leading-tight`}>
        {card.lemma}
      </div>
      <div className="mt-1 text-sm text-[var(--lf-text-muted)]">
        Tap card or "Show meaning" to flip
      </div>
    </div>
  );
}

function CardBack({ card, pronScore }) {
  return (
    <div className="flex flex-col gap-4" style={{ minHeight: 220 }}>
      <div>
        <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Meaning</div>
        <div className="mt-1 flex items-baseline gap-2">
          <Translate size={18} weight="duotone" className="text-[var(--lf-primary)]" />
          <div className="text-2xl font-bold">{card.translation}</div>
        </div>
      </div>
      {card.respelling ? (
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Sounds like</div>
          <div className="mt-1 text-base font-mono italic text-[var(--lf-text)]">
            /{card.respelling}/
          </div>
        </div>
      ) : null}
      {card.example_target ? (
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">In a sentence</div>
          <div className="mt-1 text-base italic">"{card.example_target}"</div>
          <div className="text-sm text-[var(--lf-text-muted)]">{card.example_native}</div>
        </div>
      ) : null}
      {pronScore ? null : (
        <div className="text-xs text-[var(--lf-text-muted)]">
          Hit "Press &amp; say it" to score your pronunciation.
        </div>
      )}
    </div>
  );
}

function ScoreBanner({ pronScore, target }) {
  const ok = pronScore.score >= 0.6;
  return (
    <div
      className={`rounded-2xl border p-3 text-sm ${
        ok
          ? 'border-[var(--lf-primary)] bg-[#FFF5EE] text-[var(--lf-primary)]'
          : 'border-[var(--lf-border)] bg-[var(--lf-surface-muted)] text-[var(--lf-text)]'
      }`}
      data-testid="newcomer-score-banner"
    >
      <div className="flex items-center justify-between">
        <div className="font-semibold capitalize">
          {ok ? <CheckCircle size={16} weight="fill" className="inline mr-1" /> : null}
          {pronScore.label || 'scored'} ({Math.round((pronScore.score || 0) * 100)}%)
        </div>
        <div className="text-xs opacity-70">target: {target}</div>
      </div>
      {pronScore.heard ? (
        <div className="mt-1 text-xs italic opacity-80">heard: "{pronScore.heard}"</div>
      ) : null}
    </div>
  );
}

function Loading() {
  return (
    <div className="mt-12 flex items-center gap-2 text-sm text-[var(--lf-text-muted)]">
      <SpinnerGap className="animate-spin" size={18} /> Loading your deck…
    </div>
  );
}

function Empty({ onReload }) {
  return (
    <div className="mt-12 lf-card p-6 text-center">
      <div className="text-2xl font-bold">All caught up for now ☕</div>
      <p className="mt-1 text-sm text-[var(--lf-text-muted)]">
        Come back later — your spaced cards will be ready soon.
      </p>
      <button onClick={onReload} className="lf-btn lf-btn-secondary mt-4" data-testid="newcomer-reload">
        Refresh deck
      </button>
    </div>
  );
}
